import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { message, Spin } from "antd";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import DashboardHeader from "./DashboardHeader";
import StatCard from "./StatCard";
import DailyTaskTrendChart from "./DailyTaskTrender";
import DelayBreakdownTable from "./DailyBreakDownTable";
import TaskTable from "./TaskTable";
import axiosInstance from "../../../services/axiosInstance";

const ManagerMain = () => {
  // Get user email from Redux state
  const userEmail = useSelector((state) => state?.auth?.user?.user?.email);

  const sidebarCollapsed = useSelector((state) => state?.ui?.collapsed);
  const isSideMenuOpen = sidebarCollapsed === undefined ? true : !sidebarCollapsed;

  // Default date range: this week (Monday to Sunday)
  const getThisWeekDateRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: monday, to: sunday };
  };

  const [dateRange, setDateRange] = useState(getThisWeekDateRange());
  const [datePreset, setDatePreset] = useState("this_week");
  const [selectedEmail, setSelectedEmail] = useState(null); // null means use logged-in user's email
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    summary: {
      total_tasks: 0,
      completed: 0,
      delayed: 0,
      total_words: 0,
    },
    daily_trend: [],
    delay_breakdown: [],
    tasks: [],
  });

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Email: use selected email from dropdown, or default to logged-in user's email
      const emailToUse = selectedEmail || userEmail;
      if (emailToUse) {
        params.append("email", emailToUse);
      }

      // API requires from_date and to_date
      const fromDate = dateRange.from;
      const toDate = dateRange.to;

      params.append("from_date", formatDate(fromDate));
      params.append("to_date", formatDate(toDate));

      const queryString = params.toString();
      const url = `/task/v2/ops-dashboard/${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axiosInstance.get(url);

      if (response.data.status === "success") {
        setDashboardData(response.data.data);
      } else {
        message.error("Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      message.error("Error fetching dashboard data");
    } finally {
      setLoading(false);
    }
  }, [selectedEmail, dateRange, userEmail]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleClear = () => {
    setDateRange(getThisWeekDateRange());
    setDatePreset("this_week");
    setSelectedEmail(null); // Reset to logged-in user's email
  };

  const handleEmailChange = (email) => {
    setSelectedEmail(email);
  };

  const { summary, delay_breakdown, tasks } = dashboardData;

  return (
    <Spin spinning={loading}>
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          padding: "16px",
          width: isSideMenuOpen ? "calc(100% - 20px)" : "100%",
        }}
      >
        <div style={{ margin: "0 auto",width: "100%", }}>
          <DashboardHeader
            onClear={handleClear}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            selectedEmail={selectedEmail}
            onEmailChange={handleEmailChange}
            datePreset={datePreset}
            onDatePresetChange={setDatePreset}
          />

          {/* Stats Grid */}

          {/* Charts Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
              marginBottom: "24px",
                        width: "100%",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
                marginBottom: "0px",
              }}
            >
              <StatCard
                icon={<FileTextOutlined style={{ fontSize: "24px" }} />}
                label="Total Tasks"
                value={summary.total_tasks}
                variant="primary"
              />
              <StatCard
                icon={<CheckCircleOutlined style={{ fontSize: "24px" }} />}
                label="Completed"
                value={summary.completed}
                variant="success"
              />
              <StatCard
                icon={<ClockCircleOutlined style={{ fontSize: "24px" }} />}
                label="Delayed"
                value={summary.delayed}
                variant="danger"
              />
              <StatCard
                icon={<EditOutlined style={{ fontSize: "24px" }} />}
                label="Total Words"
                value={summary.total_words?.toLocaleString() || "0"}
                variant="info"
              />
            </div>
            {/* <DailyTaskTrendChart data={daily_trend} /> */}
            <DelayBreakdownTable data={delay_breakdown} />
          </div>

          {/* Task Table */}
          <TaskTable data={tasks} />
        </div>
      </div>
    </Spin>
  );
};

export default ManagerMain;
